import { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';
import WalletPreviewDialog from './WalletPreviewDialog';
import { readWalletPreview } from './creationPayload';
import type { DefinitionFormData } from './CampaignDefinitionStep';
import type { TargetingFormData } from './CampaignTargetingStep';
import type { OfferFormData } from './offers/offerForm';
import type { ValidationActions } from './editor/useValidationActions';
import CampaignSummaryCard from './validation/CampaignSummaryCard';
import ValidationActionsCard, { ValidationAcknowledgements } from './validation/ValidationActionsCard';

interface CampaignValidationStepProps {
  definition: DefinitionFormData;
  targeting: TargetingFormData;
  offers: OfferFormData[];
  hasCreation: boolean;
  creationPayload: Record<string, unknown> | null;
  onTestOperationChange: (isTest: boolean) => void;
  acknowledgements: ValidationAcknowledgements;
  actions: ValidationActions;
}

function CampaignValidationStep({
  definition, targeting, offers, hasCreation, creationPayload, onTestOperationChange, acknowledgements, actions,
}: CampaignValidationStepProps) {
  const { t } = useLanguage();
  const [walletPreviewOpen, setWalletPreviewOpen] = useState(false);
  const isCourrier = definition.channel === 'courrier';
  const isWallet = definition.channel === 'wallet';

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {t('pages.marketing.communication.validation')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={isCourrier ? 12 : { xs: 12, md: 7 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
            {t('pages.marketing.communication.communicationSummary')}
          </Typography>
          <CampaignSummaryCard
            definition={definition}
            targeting={targeting}
            offers={offers.filter(o => o.title.trim() !== '')}
            hasCreation={hasCreation}
            creationPayload={creationPayload}
          />
        </Grid>

        {!isCourrier && (
          <Grid size={{ xs: 12, md: 5 }}>
            <ValidationActionsCard
              isWallet={isWallet}
              showBat={!(definition.channel === 'cashdesk' && definition.noCreation)}
              hasCreation={hasCreation}
              isTestOperation={definition.isTestOperation}
              onTestOperationChange={onTestOperationChange}
              acknowledgements={acknowledgements}
              actions={actions}
              onOpenWalletPreview={() => setWalletPreviewOpen(true)}
            />
          </Grid>
        )}
      </Grid>

      {isWallet && (
        <WalletPreviewDialog
          open={walletPreviewOpen}
          onClose={() => setWalletPreviewOpen(false)}
          data={readWalletPreview(creationPayload)}
        />
      )}
    </Box>
  );
}

export default CampaignValidationStep;
